import React, { useCallback, useEffect, useState } from "react";
import {
  BarSection,
  CashSection,
  CoinSection,
  GinniSection,
  GoldRateWorkSection,
  LabSection,
  PCSection,
  SilverRateWorkSection,
  SilverSection,
} from "./TransactionSection";
import { transformSectionsIntoUI } from "../../utils/sections";
import "../../css/salepoint.css";
import { myTransactionRequestUpdpated } from "../../utils/transform";
import { updateTransactionById } from "../../services/transactionService";
import { errorAlert, successAlert } from "../common/Swal";
import { useBrands } from "../../context/BrandsContext";
import { getRequiredBrands } from "../../utils/brandsFilter";
import { isCustomerSelected } from "./TransactionTable";

export function TransactionUpdate({
  type = "given",
  initialTx,
  seletectedCustomer,
  onUpdate = () => {},
}) {
  const isGiven = type === "given";
  const prefix = type;
  const heading = isGiven ? "Given" : "Received";

  const [localTx, setLocalTx] = useState({});

  const { brands, refreshBrands } = useBrands();
  const [pcBrands, setPcBrands] = useState([]);
  const [barBrands, setBarBrands] = useState([]);
  const [coinBrands, setCoinBrands] = useState([]);
  const [silverBrands, setSilverBrands] = useState([]);

  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    const pcBrands = getRequiredBrands("pc", brands);
    const barBrands = getRequiredBrands("bar", brands);
    const coinBrands = getRequiredBrands("coin", brands);
    const silverBrands = getRequiredBrands("silver", brands);

    setPcBrands(pcBrands);
    setBarBrands(barBrands);
    setCoinBrands(coinBrands);
    setSilverBrands(silverBrands);
  }, [brands]);

  useEffect(() => {
    const tx = initialTx?.[type];
    const tx_transformed = transformSectionsIntoUI(tx);
    setLocalTx(tx_transformed);
  }, []);

  const updateTransaction = async () => {
    setUpdateLoading(true);
    const r_g = {
      cash: localTx.cash,
      pc: localTx.pc,
      lab: localTx.lab,
      ginni: localTx.ginni,
      coin: localTx.coin,
      bar: localTx.bar,
      silver: localTx.silver,
      goldRW: localTx.goldRW,
      silverRW: localTx.silverRW,
    };

    try {
      const transactionId = initialTx?._id;

      const payload = myTransactionRequestUpdpated({
        customerId: "customerId",
        ...(isGiven ? { givenTx: r_g } : { receiveTx: r_g }),
        note: initialTx?.note,
      });

      if (!payload.success) {
        return errorAlert("Irrelevant transaction or missing field");
      }

      const response = await updateTransactionById(transactionId, payload.data);

      if (response.success) {
        onUpdate();
        return successAlert("Transaction Update Susccessfully");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error while updating the transaction";
      errorAlert(message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const set = useCallback(
    (section) => (valueOrUpdater) => {
      setLocalTx((prev) => {
        const next = {
          ...prev,
          [section]:
            typeof valueOrUpdater === "function"
              ? valueOrUpdater(prev[section])
              : valueOrUpdater,
        };
        return next;
      });
    },
    [type],
  );

  return (
    <>
      <div className="box pb-2">
        <h2>{heading}</h2>

        {localTx.cash && (
          <CashSection
            prefix={prefix}
            state={localTx.cash}
            setState={set("cash")}
          />
        )}

        {localTx.pc && (
          <PCSection
            prefix={prefix}
            state={localTx.pc}
            setState={set("pc")}
            brands={pcBrands}
            setBrands={setPcBrands}
            isGiven={isGiven}
            refreshBrands={refreshBrands}
          />
        )}

        {localTx.lab && (
          <LabSection
            prefix={prefix}
            state={localTx.lab}
            setState={set("lab")}
            isGiven={isGiven}
            handleReportSubmit={(transaction) => {
              if (!isCustomerSelected(seletectedCustomer?.customer)) {
                return errorAlert("Please Select the customer");
              }

              const addLab = { ...seletectedCustomer, lab: transaction };

              if (!checkCompleteField(addLab.lab)) {
                return errorAlert("Please, fill all the required fields.");
              }

              const queryData = encodeURIComponent(JSON.stringify(addLab));
              window.open(
                `/salepoint/gold-test-report?data=${queryData}`,
                "_blank",
              );
            }}
          />
        )}

        {localTx.bar && (
          <BarSection
            prefix={prefix}
            state={localTx.bar}
            setState={set("bar")}
            brands={barBrands}
            isGiven={isGiven}
            refreshBrands={refreshBrands}
          />
        )}
        {localTx.coin && (
          <CoinSection
            prefix={prefix}
            state={localTx.coin}
            setState={set("coin")}
            isGiven={isGiven}
            brands={coinBrands}
            refreshBrands={refreshBrands}
          />
        )}
        {localTx.ginni && (
          <GinniSection
            prefix={prefix}
            state={localTx.ginni}
            setState={set("ginni")}
            isGiven={isGiven}
            onReportView={(transaction) => {
              const ginni = transaction;
              if (!isCustomerSelected(seletectedCustomer?.customer)) {
                return errorAlert("Please Select the customer");
              }
              if (!ginni.report || !ginni?.weight) {
                return errorAlert(
                  "Please select the required fields to view the report.",
                );
              }

              const queryData = {
                ...seletectedCustomer,
                lab: {
                  report: ginni.report,
                  pureWeight: ginni.pureWeight,
                  weight: ginni.weight,
                },
              };

              window.open(
                `/salepoint/gold-test-report?data=${encodeURIComponent(JSON.stringify(queryData))}`,
                "_blank",
              );

              // console.log(`${prefix} Ginni Report`, state);
            }}
          />
        )}
        {localTx.silver && (
          <SilverSection
            prefix={prefix}
            state={localTx.silver}
            setState={set("silver")}
            brands={silverBrands}
            refreshBrands={refreshBrands}
          />
        )}
        {localTx.goldRW && (
          <GoldRateWorkSection
            prefix={prefix}
            state={localTx.goldRW}
            setState={set("goldRW")}
          />
        )}
        {localTx.silverRW && (
          <SilverRateWorkSection
            prefix={prefix}
            state={localTx.silverRW}
            setState={set("silverRW")}
          />
        )}
      </div>
      <button
        onClick={updateTransaction}
        className="btn btn-sm btn-warning mt-2 px-3 py-2 shadow-sm"
        style={{ borderRadius: "10px", fontWeight: "500" }}
        disabled={updateLoading}
      >
        {updateLoading ? "Updating..." : "Update"}
      </button>
    </>
  );
}

const checkCompleteField = (lab) => {
  return lab.report && lab.weight && lab.pureWeight;
};
